import Draggable from "react-draggable";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useEffect, useState, useMemo, Fragment, useRef } from "react";
import { PiStarFourDuotone } from "react-icons/pi";
import { CiCalendar } from "react-icons/ci";
import { FaCircleUser } from "react-icons/fa6";
import { FaChevronDown } from "react-icons/fa6";

import { axInst } from "../../Login/Service";
import {
  FaArrowUpRightFromSquare,
  FaMaximize,
  FaMessage,
} from "react-icons/fa6";

const SummaryDetailModal = ({ handleModal }) => {
  const [data, setData] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [comments, setComments] = useState({});
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [statusLoading, setStatusLoading] = useState({});

  const dropdownRefs = useRef({}); // refs for dropdowns

  // Fetch summary data with optional filters
  const fetchData = async (body = {}, keepExpanded = null) => {
    try {
      const res = await axInst.post(
        "/dealer-summary-dashboard/summaries",
        body
      );
      setData(res?.data || []);
      if (keepExpanded) setExpandedRows(keepExpanded);
    } catch (error) {
      console.error("Error fetching summary:", error);
      setData([]);
    }
  };

  // Fetch filter options dynamically
  const fetchFilterOptions = async () => {
    try {
      const res = await axInst.post("/dealer-summary-dashboard/summary_filter");
      const options = res?.data || {};
      setFilterOptions(options);

      // initialize filters as empty arrays
      const initialFilters = {};
      Object.keys(options).forEach((key) => (initialFilters[key] = []));
      setFilters(initialFilters);
    } catch (err) {
      console.error("Failed to fetch filter options", err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchFilterOptions();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(dropdownRefs.current).forEach((key) => {
        if (
          dropdownRefs.current[key] &&
          !dropdownRefs.current[key].contains(event.target)
        ) {
          setFilters((prev) => ({ ...prev, [`${key}_open`]: false }));
        }
      });
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Apply filters
  const applyFilters = () => {
    const body = {};
    if (filters.REGION?.length) body.region = filters.REGION;
    if (filters.STATE?.length) body.state = filters.STATE;
    if (filters.DEALER_CODE?.length) body.dealer_code = filters.DEALER_CODE;
    if (filters.DEALER_NAME?.length) body.dealer_name = filters.DEALER_NAME;
    if (filters.COMPONENT_NAME?.length) body.comp_name = filters.COMPONENT_NAME;

    fetchData(body);
  };

  // Reset filters
  const resetFilters = () => {
    const reset = {};
    Object.keys(filterOptions).forEach((key) => (reset[key] = []));
    setFilters(reset);
    fetchData();
  };

  const StatusDropdown = ({
    summaryId,
    currentStatus,
    statusOptions,
    handleUpdate,
    loading,
  }) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(currentStatus);

    // Map status to Tailwind colors
    const statusColors = {
      Assigned: "bg-blue-500 text-white",
      "In Progress": "bg-amber-400 text-white",
      Completed: "bg-green-500 text-white",
      "Re Open": "bg-red-500 text-white",
    };

    const handleSelect = async (option) => {
      setSelected(option.label);
      setOpen(false);
      await handleUpdate(summaryId, option.value, option.label);
    };

    return (
      <div className="relative ">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
          className={`w-full text-center border px-2 py-1 text-sm rounded-full flex justify-between items-center whitespace-nowrap  ${
            loading
              ? "bg-gray-100 text-gray-400"
              : statusColors[selected] || "bg-white text-gray-700"
          }`}
        >
          {selected || "Select status"}
          <FaChevronDown className="ml-2 text-xs" />
        </button>

        {open && (
          <ul className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-40 overflow-auto">
            {statusOptions.map((opt) => (
              <li
                key={opt.value}
                className="px-2 py-1 hover:bg-gray-200 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(opt);
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const statusOptions = [
    { value: 0, label: "Assigned" },
    { value: 1, label: "In Progress" },
    { value: 2, label: "Completed" },
    { value: 3, label: "Re Open" },
  ];

  // Table columns
  const columns = useMemo(
    () => [
      { header: "", accessorKey: "", id: "plus", size: 10 },
      { header: "Metrics", accessorKey: "comp_name" },
      { header: "Dealer Name", accessorKey: "dealer_name" },
      {
        header: "Summary",
        accessorKey: "summary_text",
        cell: (info) => <div className="text-left">{info.getValue()}</div>,
      },

      { header: "Due Date", accessorKey: "due_date" },
      { header: "Assigned By", accessorKey: "assign_by_user_name" },
      { header: "Assigned To", accessorKey: "assign_to_user_name" },
      {
        header: "Priority",
        accessorKey: "priority_status",
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium">
              {value}
            </span>
          );
        },
      },

      {
        header: "Status",
        accessorKey: "progress_status",
        cell: ({ row }) => {
          const summaryId = row.original.id;
          return (
            <StatusDropdown
              summaryId={summaryId}
              currentStatus={row.original.progress_status}
              statusOptions={statusOptions}
              loading={statusLoading[summaryId]}
              handleUpdate={async (id, newValue, newLabel) => {
                setStatusLoading((prev) => ({ ...prev, [id]: true }));
                try {
                  await axInst.patch(
                    `dealer-summary-dashboard/progress/${id}`,
                    {
                      progress_level: newValue, // send number directly
                    }
                  );

                  setData((prev) =>
                    prev.map((item) =>
                      item.id === id
                        ? { ...item, progress_status: newLabel }
                        : item
                    )
                  );
                } catch (err) {
                  console.error("Failed to update status", err);
                  alert("Failed to update status");
                } finally {
                  setStatusLoading((prev) => ({ ...prev, [id]: false }));
                }
              }}
            />
          );
        },
      },

      {
        header: "Comments",
        accessorKey: "comment",
        cell: ({ row }) => (
          <div className="flex items-center justify-center gap-2">
            <span>{row.original.comment}</span>
            <FaMessage className="text-gray-300" />
          </div>
        ),
      },
    ],
    [statusLoading]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const toggleRowExpanded = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const sanitizeInput = (input) => {
    return input.replace(/[^a-zA-Z0-9.\s]/g, "");
  };

  const handleCommentChange = (rowId, value) => {
    setComments((prev) => ({ ...prev, [rowId]: value }));
  };

  const handleSaveComment = async (rowOriginalId, rowId) => {
    try {
      const newComment = { message_text: comments[rowId] || "" };
      const res = await axInst.post(
        `dealer-summary-dashboard/summaries/${rowOriginalId}/replies`,
        newComment
      );

      const savedReply = res.data;
      setData((prevData) =>
        prevData.map((item) =>
          item.id === rowOriginalId
            ? { ...item, replies: [...(item.replies || []), savedReply] }
            : item
        )
      );

      setComments((prev) => ({ ...prev, [rowId]: "" }));
    } catch (err) {
      console.error(err);
      alert("Failed to save comment");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      {isMinimized ? (
        <div className="fixed bottom-4 right-4 bg-gradient-to-r from-[#8463FA] to-[#EF6AE9] shadow-lg rounded-lg w-[300px] h-[50px] flex items-center justify-between px-4">
          <span className="font-medium text-white">Action Details</span>
          <div className="flex gap-2">
            <button
              onClick={() => setIsMinimized(false)}
              className="text-white"
              title="Maximize"
            >
              <FaMaximize />
            </button>
            <button
              onClick={() => handleModal(false)}
              title="Close"
              className="text-white hover:text-gray-200 rounded-full bg-[#E5E5E570] w-6 h-6"
            >
              ✕
            </button>
          </div>
        </div>
      ) : (
        <Draggable handle=".modal-header" cancel="button" bounds="parent">
          <div className="bg-white rounded-lg shadow-lg relative md:h-[520px] h-[680px] mx-4 w-full flex flex-col">
            {/* Header */}
            <div className="modal-header cursor-move bg-gradient-to-r from-[#8463FA] to-[#EF6AE9] px-4 py-2 rounded-t-lg flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">
                Action Details
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsMinimized(true)}
                  title="Minimize"
                  className="text-white hover:text-gray-200 rounded-full w-6 h-6 flex items-center justify-center"
                >
                  <FaArrowUpRightFromSquare />
                </button>
                <button
                  onClick={() => handleModal(false)}
                  className="text-white hover:text-gray-200 rounded-full bg-[#E5E5E570] w-6 h-6"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-2 flex flex-col h-full">
              {/* Filters */}
              <div className="p-2 border border-[#2c8a8b] rounded-md flex flex-wrap gap-4 items-end justify-center">
                {Object.keys(filterOptions).map((key) => {
                  const selected = filters[key] || [];
                  const searchValue = filters[`${key}_search`] || "";
                  const allSelected =
                    selected.length === filterOptions[key].length;

                  return (
                    <div
                      key={key}
                      ref={(el) => (dropdownRefs.current[key] = el)}
                      className="relative w-[150px] flex flex-col gap-1"
                    >
                      <label className="text-xs font-medium text-gray-600">
                        {key.replaceAll("_", " ")}
                      </label>

                      <button
                        type="button"
                        className="w-full text-xs border rounded px-2 py-1 text-left flex justify-between items-center bg-white"
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            [`${key}_open`]: !prev[`${key}_open`],
                            [`${key}_search`]: "",
                          }))
                        }
                      >
                        <span className="truncate">
                          {selected.length === 1
                            ? selected[0]
                            : `Selected (${selected.length})`}
                        </span>
                      </button>

                      {filters[`${key}_open`] && (
                        <div className="absolute z-50 mt-1 w-full bg-white border rounded shadow max-h-60 overflow-y-auto p-2 scrollbar-none">
                          <input
                            type="text"
                            placeholder="Search..."
                            className="w-full p-1 mb-2 border rounded text-xs"
                            value={searchValue}
                            onChange={(e) =>
                              setFilters((prev) => ({
                                ...prev,
                                [`${key}_search`]: e.target.value,
                              }))
                            }
                          />

                          <label className="flex items-center gap-2 p-1 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={() =>
                                setFilters((prev) => ({
                                  ...prev,
                                  [key]: allSelected
                                    ? []
                                    : [...filterOptions[key]],
                                }))
                              }
                            />
                            <span>Select All</span>
                          </label>

                          {filterOptions[key]
                            .filter((opt) =>
                              searchValue
                                ? opt
                                    .toLowerCase()
                                    .includes(searchValue.toLowerCase())
                                : true
                            )
                            .map((option) => (
                              <label
                                key={option}
                                className="flex items-center gap-2 p-1 hover:bg-gray-100 cursor-pointer text-xs"
                              >
                                <input
                                  type="checkbox"
                                  checked={selected.includes(option)}
                                  onChange={(e) => {
                                    const updated = e.target.checked
                                      ? [...selected, option]
                                      : selected.filter((v) => v !== option);
                                    setFilters((prev) => ({
                                      ...prev,
                                      [key]: updated,
                                    }));
                                  }}
                                />
                                {option}
                              </label>
                            ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="flex items-center gap-2">
                  <button
                    onClick={applyFilters}
                    className="px-3 py-1 bg-[#2A5775] text-white rounded text-sm"
                  >
                    Apply
                  </button>
                  <button
                    onClick={resetFilters}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="mt-2 flex-1 overflow-y-auto border border-gray-200 max-h-[390px] scrollbar-none rounded-lg shadow-sm">
                <table className="w-full divide-y divide-gray-200 text-sm  ">
                  <thead className="bg-[#8463FA] text-white sticky top-0 z-10">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className="px-2 py-2 text-center font-semibold text-white uppercase tracking-wider"
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {table.getRowModel().rows.length > 0 ? (
                      table.getRowModel().rows.map((row, idx) => (
                        <Fragment key={row.id}>
                          <tr
                            className={`cursor-pointer hover:bg-blue-50 ${
                              idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }`}
                            onClick={() => toggleRowExpanded(row.id)}
                          >
                            {row.getVisibleCells().map((cell, cellIdx) => (
                              <td
                                key={cell.id}
                                className="px-2 py-1 text-center text-gray-600 "
                              >
                                {cellIdx === 0 ? (
                                  <span className="flex items-center justify-end gap-2 text-sm  font-medium text-gray-700">
                                    <span className="w-4 h-4 flex items-center justify-center bg-gray-300 text-gray-700 font-bold rounded-full">
                                      {expandedRows[row.id] ? "−" : "+"}
                                    </span>
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )}
                                  </span>
                                ) : (
                                  flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )
                                )}
                              </td>
                            ))}
                          </tr>

                          {expandedRows[row.id] && (
                            <tr>
                              <td
                                colSpan={columns.length}
                                className="bg-gray-200 px-48 py-4 border-t"
                              >
                                <div className=" mb-3 w-full">
                                  {row.original.replies?.length > 0 ? (
                                    row.original.replies.map((reply) => (
                                      <div className="flex gap-2 w-full mb-2">
                                        <FaCircleUser className="text-gray-600 text-2xl" />
                                        <div
                                          key={reply.id}
                                          className="p-2 bg-white border rounded shadow-sm text-sm w-full"
                                        >
                                          <div className="flex justify-between items-center w-full">
                                            <span className="text-xs font-medium text-gray-700">
                                              {reply.sender_id}
                                            </span>
                                            <span className="text-xs text-gray-400 flex gap-1 items-center">
                                              <CiCalendar className="text-sm" />
                                              {new Date(
                                                reply.timestamp
                                              ).toLocaleString()}
                                            </span>
                                          </div>
                                          <p className="mt-1 text-gray-600">
                                            {reply.message_text}
                                          </p>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-gray-800 text-sm italic flex items-center gap-1">
                                      No comments yet.
                                      <PiStarFourDuotone />
                                    </p>
                                  )}
                                </div>

                                {/* <textarea
                                  value={comments[row.id] || ""}
                                  onChange={(e) =>
                                    handleCommentChange(row.id, e.target.value)
                                  }
                                  placeholder="Add your comment..."
                                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
                                /> */}

                                <textarea
                                  value={comments[row.id] || ""}
                                  onChange={(e) => {
                                    const sanitizedValue = sanitizeInput(
                                      e.target.value
                                    );
                                    handleCommentChange(row.id, sanitizedValue);
                                  }}
                                  placeholder="Add your comment..."
                                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
                                />

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveComment(row.original.id, row.id);
                                  }}
                                  className="mt-2 px-4 py-1 bg-[#00A16C] hover:bg-blue-700 text-white font-medium rounded-md transition"
                                >
                                  Save Comment
                                </button>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="text-center py-6 text-gray-400"
                        >
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Draggable>
      )}
    </div>
  );
};

export default SummaryDetailModal;
